import { observer } from 'mobx-react-lite';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createStorePerformanceRequest, StorePerformanceInterval } from '../../../../../constants';
import {
  ConversionRateOverviewResponse,
  KpiOverviewResponse,
  MemberOverviewResponse,
  QueueCustomerOverviewResponse,
  RfidOverviewResponse,
  SalesOverviewResponse,
  SummaryWidgetsResponse,
  useAsyncAction,
  useStoreInformation,
  useTransaction,
} from '../../../../../hooks';
import { useCurrencyTools } from '../../../../../hooks/useCurrencyTools';
import { Icons, Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { areaOverviewTranslations } from '../../../../../translations/areaOverview';
import { isFailureResponse } from '../../../../../utils';
import { OverviewWidget } from '../../../../areaOverview/overviewWidget/OverviewWidget';
import { AreaWidgetType } from '../../../../areaOverview/util';
import { StyledGrid } from './KpiOverviewWidget.styles';
import { ForecastCard } from './forecastCard/ForecastCard';

const getDailyConversionRate = (data: ConversionRateOverviewResponse | null) => {
  if (!data) return null;
  return data.totalVisitors ? (data.totalTransactions / data?.totalVisitors) * 100 : 0;
};

const getDailyTurnOver = (data: SalesOverviewResponse | null) => {
  if (!data) return null;
  return data.regularSales + data.scoSales;
};

const getDailySCOTurnOver = (data: SalesOverviewResponse | null) => {
  if (!data) return null;
  return data.scoSales;
};

const getDailyShareOfReceipts = (data: MemberOverviewResponse | null) => {
  if (!data) return null;
  return data.totalTransactions !== 0 ? (data.memberTransactions / data.totalTransactions) * 100 : 0;
};

const getDailySCOShareOfReceipts = (data: MemberOverviewResponse | null) => {
  if (!data) return null;
  return data.totalTransactions !== 0 ? (data.scoTransactions / data.totalTransactions) * 100 : 0;
};

const getDailyQueueLength = (data: QueueCustomerOverviewResponse | null) => {
  if (!data) return null;
  return data.overallQueueLength;
};

const getDailyCustomerEngagement = (data: QueueCustomerOverviewResponse | null) => {
  if (!data) return null;
  return data ? data.customerEngagement : 0;
};

const getDailyRFIDAddedSales = (data: RfidOverviewResponse | null) => {
  if (!data) return null;

  return data.rfidSales;
};

const getDailyNewMembers = (data: SummaryWidgetsResponse | null) => {
  if (!data) return null;
  return data.totalNewMembers;
};

type KpiOverviewWidgetProps = {
  currentDate: Date;
  interval: StorePerformanceInterval;
  conversionRateOverviewData: ConversionRateOverviewResponse | null;
  salesOverviewData: SalesOverviewResponse | null;
  membersOverviewData: MemberOverviewResponse | null;
  queueCustomerOverviewData: QueueCustomerOverviewResponse | null;
  rfidOverviewData: RfidOverviewResponse | null;
  summaryWidgetsData: SummaryWidgetsResponse | null;
};

export const KpiOverviewWidget: FC<KpiOverviewWidgetProps> = observer(
  ({
    currentDate,
    interval,
    conversionRateOverviewData,
    salesOverviewData,
    membersOverviewData,
    queueCustomerOverviewData,
    rfidOverviewData,
    summaryWidgetsData,
  }) => {
    const { t } = useTranslation();
    const { fetchKpiOverviewData } = useTransaction();
    const { currencyCode: targetCurrency } = useStoreInformation();
    const { getFormattedCurrency } = useCurrencyTools(interval, currentDate);

    const {
      result: kpiOverviewData,
      isLoading: kpiOverviewLoading,
      isError: kpiOverviewError,
      triggerAction: triggerFetchKpiOverview,
    } = useAsyncAction<KpiOverviewResponse>();

    const fetchData = async () => {
      const request = createStorePerformanceRequest({
        interval,
        currentDate,
      });
      const response = await fetchKpiOverviewData(request, targetCurrency);

      if (isFailureResponse(response)) {
        throw new Error();
      }

      return response.data;
    };

    const fetchWidgetData = () => {
      triggerFetchKpiOverview(fetchData);
    };

    useEffect(() => {
      fetchWidgetData();
    }, [currentDate]);

    const dailyConversionRate = getDailyConversionRate(conversionRateOverviewData);

    const dailyTurnOver = getDailyTurnOver(salesOverviewData);

    const dailySCOTurnOver = getDailySCOTurnOver(salesOverviewData);

    const dailyShareOfReceipts = getDailyShareOfReceipts(membersOverviewData);

    const dailySCOShareOfReceipts = getDailySCOShareOfReceipts(membersOverviewData);

    const dailyQueueLength = getDailyQueueLength(queueCustomerOverviewData);

    const dailyCustomerEngagement = getDailyCustomerEngagement(queueCustomerOverviewData);

    const dailyRFIDAddedSales = getDailyRFIDAddedSales(rfidOverviewData);

    const dailyNewMembers = getDailyNewMembers(summaryWidgetsData);

    return (
      <OverviewWidget
        id={AreaWidgetType.KPIForecastOverview}
        isLoading={kpiOverviewLoading}
        isError={kpiOverviewError}
        errorRetryHandler={() => {
          fetchWidgetData();
        }}
        title={t(
          areaOverviewTranslations.kpiOverview.title.key,
          areaOverviewTranslations.kpiOverview.title.defaultValue,
        )}
        headerIcon={Icons.TARGET}
        customContent={
          <ColumnFlexContainer gap={[1]}>
            <Typography margin={[0, 0, 0]}>
              {t(
                areaOverviewTranslations.kpiOverview.widgetText.key,
                areaOverviewTranslations.kpiOverview.widgetText.defaultValue,
              )}
            </Typography>
            <StyledGrid gap={[2]} maxHeight={[54]} overflow="scroll">
              {kpiOverviewData && (
                <>
                  <ForecastCard
                    kpiName="dailyConversionRate"
                    kpiDetails={kpiOverviewData.dailyConversionRate}
                    currentValue={dailyConversionRate}
                  />

                  <ForecastCard
                    kpiName="dailyNewMembers"
                    kpiDetails={kpiOverviewData.dailyNewMembers}
                    currentValue={dailyNewMembers}
                  />

                  <ForecastCard
                    kpiName="dailyTurnOver"
                    kpiDetails={kpiOverviewData.dailyTurnOver}
                    currentValue={dailyTurnOver}
                    currencyFormatter={getFormattedCurrency}
                  />

                  <ForecastCard
                    kpiName="dailyShareOfReceipts"
                    kpiDetails={kpiOverviewData.dailyShareOfReceipts}
                    currentValue={dailyShareOfReceipts}
                  />

                  <ForecastCard
                    kpiName="dailyQueueLength"
                    kpiDetails={kpiOverviewData.dailyQueueLength}
                    currentValue={dailyQueueLength}
                  />

                  <ForecastCard
                    kpiName="dailySCOTurnOver"
                    kpiDetails={kpiOverviewData.dailySCOTurnOver}
                    currentValue={dailySCOTurnOver}
                    currencyFormatter={getFormattedCurrency}
                  />

                  <ForecastCard
                    kpiName="dailySCOShareOfReceipts"
                    kpiDetails={kpiOverviewData.dailySCOShareOfReceipts}
                    currentValue={dailySCOShareOfReceipts}
                  />

                  <ForecastCard
                    kpiName="dailyCustomerEngagement"
                    kpiDetails={kpiOverviewData.dailyCustomerEngagement}
                    currentValue={dailyCustomerEngagement}
                  />

                  <ForecastCard
                    kpiName="dailyRFIDAddedSales"
                    kpiDetails={kpiOverviewData.dailyRFIDAddedSales}
                    currentValue={dailyRFIDAddedSales}
                  />
                </>
              )}
            </StyledGrid>
          </ColumnFlexContainer>
        }
      />
    );
  },
);
